import { permissionEngine } from '@/core/permissions/permissionEngine';
import { useAuth } from '@/core/auth/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Leaf, PackagePlus, Calendar, Info, MapPin, Box, ChevronRight } from 'lucide-react';
import { useInventoryStockIn } from './hooks/useInventoryStockIn';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useState } from 'react';
import { cn } from '@/utils/cn';

export function InventoryStockInPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.stock_in');

 const { id = '' } = useParams();
 const navigate = useNavigate();
 const {
 item,
 values,
 setValues,
 isLoading,
 isSaving,
 submitStockIn,
 newStock,
 } = useInventoryStockIn(id);
 const [confirmOpen, setConfirmOpen] = useState(false);

 const handleSave = async () => {
 const success = await submitStockIn();
 if (success) {
 setConfirmOpen(false);
 navigate('/inventory/items');
 }
 };

 if (isLoading) {
 return (
 <div className="flex flex-col gap-4 pb-8 animate-pulse">
 <div className="h-20 rounded-md bg-white p-3" />
 <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_360px]">
 <div className="h-96 rounded-md bg-white" />
 <div className="h-96 rounded-md bg-white" />
 </div>
 </div>
 );
 }

 if (!item) {
 return (
 <div className="flex flex-col items-center justify-center p-20 text-slate-500 bg-white rounded-md border border-slate-200">
 <Info className="h-12 w-12 mb-4 opacity-20" />
 <p className="">Không tìm thấy thông tin vật tư.</p>
 <button onClick={() => navigate('/inventory/items')} className="mt-4 text-emerald-600 hover:underline">Quay lại danh sách</button>
 </div>
 );
 }

 return (
 <div className="flex flex-col gap-4 pb-8">
 {/* Premium Header */}
 <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3">
 <div className="flex items-center gap-4">
 <button
 type="button"
 onClick={() => navigate('/inventory/items')}
 className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
 >
 <ArrowLeft className="h-4 w-4" />
 </button>
 <div>
 <div className="flex items-center gap-2 text-xs text-slate-400 tracking-wider">
 <span>Kho vật tư</span>
 <ChevronRight className="h-3 w-3" />
 <span className="text-emerald-600">Nhập kho</span>
 </div>
 <h1 className="sm:text-sm text-xs text-slate-900 tracking-tight">Phiếu nhập kho vật tư</h1>
 </div>
 </div>
 <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-600 border border-emerald-100">
 <PackagePlus className="h-4 w-4" />
 <span className="sm:text-sm text-xs">Giao dịch nhập</span>
 </div>
 </div>

 <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_360px]">
 {/* Left Side: Professional Form */}
 <div className="space-y-4">
 <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
 <div className="border-b border-slate-100 bg-slate-50/50 px-4 py-3">
 <h2 className="flex items-center gap-2 text-slate-900">
 <Info className="h-4 w-4 text-emerald-600" />
 Chi tiết phiếu nhập
 </h2>
 </div>
 
 <div className="p-4 space-y-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <label className="sm:text-sm text-xs text-slate-700 flex items-center gap-1.5">
 Số lượng nhập <span className="text-rose-500">*</span>
 </label>
 <div className="relative">
 <input
 type="number"
 min={0}
 value={values.quantity || ''}
 onChange={(e) => setValues(prev => ({ ...prev, quantity: Number(e.target.value) }))}
 className="w-full rounded-md border border-slate-200 p-3 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 focus:outline-none transition-all pr-12"
 placeholder="0"
 />
 <div className="absolute right-3 top-1/2 -translate-y-1/2 sm:text-sm text-xs text-slate-400">
 {item.unit}
 </div>
 </div>
 </div>

 <div className="space-y-2">
 <label className="sm:text-sm text-xs text-slate-700 flex items-center gap-1.5">
 Đơn giá nhập <span className="text-slate-400 font-normal">(Tùy chọn)</span>
 </label>
 <div className="relative">
 <input
 type="number"
 min={0}
 value={values.unit_cost || ''}
 onChange={(e) => setValues(prev => ({ ...prev, unit_cost: Number(e.target.value) }))}
 className="w-full rounded-md border border-slate-200 p-3 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all pr-12"
 placeholder="0"
 />
 <div className="absolute right-3 top-1/2 -translate-y-1/2 sm:text-sm text-xs text-slate-400">
 VNĐ
 </div>
 </div>
 </div>

 <div className="space-y-2">
 <label className="sm:text-sm text-xs text-slate-700 flex items-center gap-1.5">
 Ngày nhập kho <span className="text-rose-500">*</span>
 </label>
 <div className="relative">
 <input
 type="date"
 value={values.transaction_date}
 onChange={(e) => setValues(prev => ({ ...prev, transaction_date: e.target.value }))}
 className="w-full rounded-md border border-slate-200 p-3 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all pl-10"
 />
 <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
 </div>
 </div>
 </div>

 <div className="space-y-2 pt-2 border-t border-slate-50">
 <label className="sm:text-sm text-xs text-slate-700 flex items-center gap-1.5">
 <MapPin className="h-4 w-4 text-slate-400" />
 Đ9a iỒm lưu trữ
 </label>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
 <p className="text-xs text-slate-400">Kho hàng</p>
 <p className="text-slate-900">{item.warehouse_name || 'Chưa thiết lập'}</p>
 </div>
 <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
 <p className="text-xs text-slate-400">Vị trí cụ thể</p>
 <p className="text-slate-900">{item.storage_location || 'Mặc định'}</p>
 </div>
 </div>
 </div>

 <div className="space-y-2">
 <label className="sm:text-sm text-xs text-slate-700">Ghi chú giao d9ch</label>
 <textarea
 value={values.note}
 onChange={(e) => setValues(prev => ({ ...prev, note: e.target.value }))}
 rows={4}
 className="w-full rounded-md border border-slate-200 p-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none transition-all resize-none"
 placeholder="Lý do nhập kho, thông tin lô hàng, chứng từ i kèm..."
 />
 </div>
 </div>
 </div>
 </div>

 {/* Right Side: Material Summary & Visual Feedback */}
 <div className="space-y-4">
 <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
 <div className="p-4 space-y-4">
 <div className="flex h-48 w-full items-center justify-center overflow-hidden rounded-md bg-slate-50 border border-slate-100 text-emerald-600">
 {item.thumbnail_url ? (
 <img src={item.thumbnail_url} alt={item.name} className="h-full w-full object-cover" />
 ) : (
 <Leaf className="h-16 w-16 opacity-10" />
 )}
 </div>
 
 <div className="space-y-1">
 <div className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-sm inline-block">
 {item.code}
 </div>
 <h3 className="text-slate-950 leading-tight">{item.name}</h3>
 <p className="text-xs text-slate-500">Đơn vị: {item.unit}</p>
 </div>

 <div className="space-y-3 pt-4 border-t border-slate-100">
 <div className="flex justify-between items-center sm:text-sm text-xs">
 <span className="text-slate-500">Tn kho hiện tại:</span>
 <span className="text-slate-900 bg-slate-100 px-2 py-0.5 rounded-sm">{item.current_stock.toLocaleString()}</span>
 </div>
 <div className="flex justify-between items-center sm:text-sm text-xs">
 <span className="text-slate-500">Số lượng nhập:</span>
 <span className="text-emerald-600">+{values.quantity || 0}</span>
 </div>
 <div className={cn(
"flex justify-between items-center rounded-md p-3 mt-4 transition-all",
 values.quantity > 0 ?"bg-emerald-600 text-white -emerald-600/20" :"bg-slate-50 text-slate-400"
 )}>
 <span className="text-xs tracking-wider opacity-80">Dự kiến sau nhập</span>
 <span className="sm:text-sm text-xs">{newStock.toLocaleString()} {item.unit}</span>
 </div>
 </div>
 </div>
 </div>

 <div className="rounded-md border border-emerald-100 bg-emerald-50/50 p-4">
 <div className="flex gap-3 text-emerald-800">
 <Box className="h-5 w-5 shrink-0 mt-0.5" />
 <div className="space-y-1">
 <p className="sm:text-sm text-xs">Lưu ý khiệnhập kho</p>
 <p className="text-xs leading-relaxed opacity-80">KiỒm tra kỹ tình trạng hàng hóa và số lượng thực tế trước khi xác nhận nhập kho Ồ ảm bảo dữ liệu tồn kho chính xác.</p>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Premium Sticky Footer Actions */}
 <div className="sticky bottom-4 z-10 mt-4 flex items-center justify-end gap-3 rounded-md border border-slate-200 bg-white/80 backdrop-blur-md p-3">
 <button
 type="button"
 onClick={() => navigate('/inventory/items')}
 className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-6 sm:text-sm text-xs text-slate-700 hover:bg-slate-50 transition-all"
 >
 Hủy bỏ
 </button>
 <button
 type="button"
 onClick={() => setConfirmOpen(true)}
 disabled={isSaving || values.quantity <= 0}
 className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-emerald-700 px-10 sm:text-sm text-xs text-white -emerald-700/20 hover:bg-emerald-800 disabled:opacity-50 disabled:grayscale transition-all active:scale-95"
 >
 <Save className="h-4 w-4" />
 Xác nhận nhập kho
 </button>
 </div>

 <ConfirmDialog
 isOpen={confirmOpen}
 title="Xác nhận nhập kho"
 message={`Bạn đang thực hiện nhập thêm ${values.quantity} ${item.unit} vào hệ thốngốcho vật tư "${item.name}". Thao tác này sẽ cập nhật tồn kho tức thì. Bạn có chắc chắn?`}
 confirmText="Hoàn tất nhập kho"
 isLoading={isSaving}
 onConfirm={handleSave}
 onClose={() => setConfirmOpen(false)}
 />
 </div>
 );
}

export default InventoryStockInPage;

